import { useState } from 'react';

// Types
export interface Person {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  talents: Array<{
    talent_id: number;
    talent_navn: string;
    talent_kategori: string;
    erfaringsnivaa: string;
    sertifisert: boolean;
  }>;
  allokertTalent?: string;
  /**
   * Kun relevant for `modified`-endringer: identifiserer entydig hvilken
   * produksjon_bemanning-rad som skal endres. En person kan ha flere roller
   * (rader) i samme produksjon, så uten en av disse to feltene kan ikke
   * backend vite hvilken rad som er ment - se backend/modules/produksjon/draft/service.js.
   * `bemanningId` (produksjon_bemanning.id) er foretrukket når den er kjent;
   * `originalTalentNavn` (talent_navn FØR endringen) er et fallback-alternativ.
   */
  bemanningId?: number;
  originalTalentNavn?: string;
}

export interface DraftChanges {
  added: Person[];
  removed: Person[];
  modified: Person[];
}

export interface SelectedTalent {
  talent_navn: string;
  talent_kategori: string;
  antall: number;
  antallFylt: number;
}

/**
 * Custom hook for managing draft bemanning (staffing) changes.
 * 
 * Replaces the global DraftContext with local state management.
 * Uses simple useState instead of useReducer for better simplicity.
 * 
 * @example
 * ```tsx
 * const draft = useDraftBemanning();
 * 
 * // Enter draft mode
 * draft.enterDraftMode(prodId, talentInfo, existingList);
 * 
 * // Make changes
 * draft.addPerson(person);
 * draft.removePerson(personId);
 * 
 * // Exit or reset
 * draft.exitDraftMode();
 * draft.undoChanges();
 * ```
 */
export const useDraftBemanning = () => {
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<DraftChanges>({
    added: [],
    removed: [],
    modified: []
  });
  const [originalList, setOriginalList] = useState<Person[]>([]);
  const [selectedTalent, setSelectedTalent] = useState<SelectedTalent | undefined>(undefined);
  const [produksjonId, setProduksjonId] = useState<number | undefined>(undefined);
  
  // Computed property
  const hasUnsavedChanges = 
    pendingChanges.added.length > 0 || 
    pendingChanges.removed.length > 0 ||
    pendingChanges.modified.length > 0;
  
  /**
   * Enter draft mode for a specific talent in a production.
   */
  const enterDraftMode = (
    prodId: number, 
    talent: SelectedTalent, 
    list: Person[]
  ) => {
    setIsDraftMode(true);
    setProduksjonId(prodId);
    setSelectedTalent(talent);
    setOriginalList(list);
    setPendingChanges({ added: [], removed: [], modified: [] });
  };
  
  /**
   * Exit draft mode and clear all state.
   */
  const exitDraftMode = () => {
    setIsDraftMode(false);
    setProduksjonId(undefined);
    setSelectedTalent(undefined);
    setOriginalList([]);
    setPendingChanges({ added: [], removed: [], modified: [] });
  };
  
  /**
   * Add a person to pending changes.
   * Prevents duplicates and handles removed persons correctly.
   */
  const addPerson = (person: Person) => {
    setPendingChanges(prev => {
      // Ikke tillat duplikater: samme person OG samme talent regnes som duplikat
      const talentNavn = (person as any).talent_navn;
      const isDuplicate = prev.added.some(
        p => p.id === person.id && (p as any).talent_navn === talentNavn
      );
      if (isDuplicate) return prev;

      // Hvis personen var markert som fjernet, fjern fra removed
      const filteredRemoved = prev.removed.filter(p => p.id !== person.id);

      return {
        ...prev,
        added: [...prev.added, person],
        removed: filteredRemoved
      };
    });
  };
  
  /**
   * Remove a person from pending changes or mark for removal.
   */
  const removePerson = (personId: number) => {
    setPendingChanges(prev => {
      const newRemoved = [...prev.removed];
      
      // Hvis personen var i originalList, legg til i removed
      const originalPerson = originalList.find(p => p.id === personId);
      if (originalPerson && !newRemoved.some(p => p.id === personId)) {
        newRemoved.push(originalPerson);
      }
      
      // Fjern fra added hvis personen var der
      const filteredAdded = prev.added.filter(p => p.id !== personId);
      
      return {
        ...prev,
        added: filteredAdded,
        removed: newRemoved
      };
    });
  };
  
  /**
   * Modify a person in pending changes.
   */
  const modifyPerson = (person: Person) => {
    setPendingChanges(prev => {
      const filteredModified = prev.modified.filter(p => p.id !== person.id);
      
      return {
        ...prev,
        modified: [...filteredModified, person]
      };
    });
  };
  
  /**
   * Undo all pending changes.
   */
  const undoChanges = () => {
    setPendingChanges({ added: [], removed: [], modified: [] });
  };
  
  /**
   * Reset all changes (alias for undoChanges for backward compatibility).
   */
  const resetChanges = () => {
    setPendingChanges({ added: [], removed: [], modified: [] });
  };
  
  /**
   * Update the original list (e.g., after fetching fresh data).
   */
  const updateOriginalList = (list: Person[]) => {
    setOriginalList(list);
  };
  
  return {
    // State
    isDraftMode,
    pendingChanges,
    originalList,
    hasUnsavedChanges,
    selectedTalent,
    produksjonId,
    
    // Actions
    enterDraftMode,
    exitDraftMode,
    addPerson,
    removePerson,
    modifyPerson,
    undoChanges,
    resetChanges,
    setOriginalList: updateOriginalList
  };
};

export type UseDraftBemanningReturn = ReturnType<typeof useDraftBemanning>;

